// React / Next
import { useState, useEffect, useRef } from 'react';
import { useNavigate, getRouteApi } from '@tanstack/react-router';

// Components
import { Typography } from '@archieai/ui';
import { TextMaskReveal } from '../../components/ui/TextMaskReveal/TextMaskReveal';
import { BlogCard } from '../../components/BlogCard/BlogCard';
import { FeaturedCard } from '../../components/FeaturedCard/FeaturedCard';
import { SignupCallout } from '../../components/SignupCallout/SignupCallout';
import { HireArchieCTA } from '../../components/HireArchieCTA/HireArchieCTA';
import { SEO } from '../../components/SEO/SEO';

// Motion
import { motion } from 'motion/react';

// Hooks
import { useTrackEvent } from '../../hooks/useTrackEvent';

// Utils
import { cn } from '../../utils/cn';

// Types
import type { ArticleCard, Category, ArticleType, AuthorListItem } from '../../lib/sanity';

// Route API for accessing search params and loader data
const routeApi = getRouteApi('/resources');

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.3,
    },
  },
};

const buttonVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: 'spring' as const,
      stiffness: 400,
      damping: 25,
    },
  },
};

const filterVariants = {
  hidden: { opacity: 0, y: 15 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring' as const,
      stiffness: 300,
      damping: 25,
    },
  },
};

const cardContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring' as const,
      stiffness: 300,
      damping: 30,
    },
  },
};

// Helper function to format categories as tags string
const formatCategoriesAsTags = (categories: { title: string }[] | null | undefined): string => {
  if (!categories || categories.length === 0) return '';
  return categories.map((cat) => cat.title).join(' | ');
};

// Client-side search filter for articles (avoids loader re-run on every keystroke)
const filterArticlesBySearch = (articles: ArticleCard[], searchTerm: string): ArticleCard[] => {
  if (!searchTerm.trim()) return articles;

  const term = searchTerm.toLowerCase();

  return articles.filter((article) => {
    const title = article.title?.toLowerCase() ?? '';
    const excerpt = article.excerpt?.toLowerCase() ?? '';
    const authorName = article.author?.name?.toLowerCase() ?? '';
    const categoryNames = article.categories?.map((c) => c.title.toLowerCase()).join(' ') ?? '';

    return (
      title.includes(term) ||
      excerpt.includes(term) ||
      authorName.includes(term) ||
      categoryNames.includes(term)
    );
  });
};

/**
 * ResourcesPage - Blog/Resources listing page
 * Features a hero section, filters, featured post, and grid of blog cards
 * Fetches data from Sanity CMS
 */
export const ResourcesPage = () => {
  const navigate = useNavigate({ from: '/resources' });
  const { type, category, author, search } = routeApi.useSearch();
  const { articles, categories, authors } = routeApi.useLoaderData();
  const trackEvent = useTrackEvent();

  // Local state for search input (filtering is done client-side for instant results)
  const [searchInput, setSearchInput] = useState(search ?? '');
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync local state when URL search param changes externally
  useEffect(() => {
    setSearchInput(search ?? '');
  }, [search]);

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const resourceTypes: { value: ArticleType; label: string }[] = [
    { value: 'playbook', label: 'Playbooks' },
    { value: 'news', label: 'News' },
    { value: 'article', label: 'Articles' },
  ];

  // Check if any filter is active
  const hasActiveFilters = type || category || author || searchInput;

  // Filter articles client-side by search text (instant, no loader re-run)
  const filteredArticles = filterArticlesBySearch(articles, searchInput);

  // Get the article explicitly marked as featured via isFeatured flag (only when no filters are active)
  const featuredArticle = !hasActiveFilters
    ? filteredArticles.find((a) => a.isFeatured) ?? null
    : null;
  const displayArticles = featuredArticle
    ? filteredArticles.filter((a) => a._id !== featuredArticle._id)
    : filteredArticles;

  // Show empty state when filters are active but no results
  const showEmptyState = hasActiveFilters && filteredArticles.length === 0;

  // Update URL search params
  const updateSearch = (updates: {
    type?: ArticleType;
    category?: string;
    author?: string;
    search?: string;
  }) => {
    navigate({
      search: (prev) => {
        const newSearch = { ...prev };
        
        // Handle type
        if ('type' in updates) {
          if (updates.type) {
            newSearch.type = updates.type;
          } else {
            delete newSearch.type;
          }
        }
        
        // Handle category
        if ('category' in updates) {
          if (updates.category) {
            newSearch.category = updates.category;
          } else {
            delete newSearch.category;
          }
        }
        
        // Handle author
        if ('author' in updates) {
          if (updates.author) {
            newSearch.author = updates.author;
          } else {
            delete newSearch.author;
          }
        }
        
        // Handle search
        if ('search' in updates) {
          if (updates.search) {
            newSearch.search = updates.search;
          } else {
            delete newSearch.search;
          }
        }
        
        return newSearch;
      },
      replace: true,
    });
  };

  const handleTypeClick = (clickedType: ArticleType) => {
    const newValue = type === clickedType ? undefined : clickedType;
    trackEvent('resource_filtered', { filter_type: 'type', filter_value: newValue ?? 'cleared' });
    updateSearch({ type: newValue });
  };

  const handleCategoryChange = (value: string) => {
    trackEvent('resource_filtered', { filter_type: 'category', filter_value: value || 'cleared' });
    updateSearch({ category: value || undefined });
  };

  const handleAuthorChange = (value: string) => {
    trackEvent('resource_filtered', { filter_type: 'author', filter_value: value || 'cleared' });
    updateSearch({ author: value || undefined });
  };

  const handleSearchChange = (value: string) => {
    // Update local state immediately — filtering is instant (client-side)
    setSearchInput(value);

    // Clear any pending debounce
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Debounce the URL update for shareability (no loader re-run)
    debounceTimeoutRef.current = setTimeout(() => {
      if (value.trim()) {
        trackEvent('resource_searched', { search_term: value.trim() });
      }
      updateSearch({ search: value || undefined });
    }, 300);
  };

  const handleClearSearch = () => {
    // Clear pending debounce
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    setSearchInput('');
    updateSearch({ search: undefined });
  };

  const handleClearAllFilters = () => {
    // Clear pending debounce
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    setSearchInput('');
    navigate({
      search: {},
      replace: true,
    });
  };

  return (
    <div className="flex flex-col items-center w-full bg-white">
      <SEO
        title="Resources"
        description="Practical guides, insights, and resources to help accountants and their firms use AI with confidence."
        keywords="AI accounting resources, accounting automation guides, AI playbooks, accounting firm insights"
        canonicalUrl="/resources"
      />

      {/* Hero Section */}
      <section className="flex flex-col items-center gap-[var(--spacing-24)] px-[var(--spacing-24)] pt-[var(--spacing-64)] pb-[var(--spacing-32)] w-full max-w-[1016px] max-md:px-[var(--spacing-16)] max-md:pt-[var(--spacing-48)]">
        {/* Animated Hero Text */}
        <div className="flex flex-col items-center gap-[var(--spacing-24)] text-center">
          <TextMaskReveal
            as="h1"
            variant="display"
            className="text-blue-1000"
            staggerDelay={0.15}
            duration={0.9}
            animateOnMount
          >
            Resources
          </TextMaskReveal>
          <TextMaskReveal
            as="p"
            variant="h2"
            className="text-blue-1000 max-w-[804px]"
            staggerDelay={0.1}
            duration={0.8}
            delay={0.3}
            animateOnMount
          >
            {`Practical guides, insights, and resources to help accountants and their firms use AI with confidence.`}
          </TextMaskReveal>
        </div>

        {/* Resource Type Buttons - Animated */}
        <motion.div
          className="flex items-center gap-[var(--spacing-16)] flex-wrap justify-center"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {resourceTypes.map((resourceType) => (
            <motion.button
              key={resourceType.value}
              variants={buttonVariants}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleTypeClick(resourceType.value)}
              className={cn(
                'inline-flex items-center justify-center px-[var(--spacing-16)] py-[var(--spacing-12)] rounded-8 font-body text-[16px] leading-[24px] font-normal transition-colors duration-200 border',
                type === resourceType.value
                  ? 'bg-blue-1000 text-white border-blue-1000'
                  : 'bg-white text-blue-1000 border-blue-1000 hover:bg-gray-50'
              )}
            >
              {resourceType.label}
            </motion.button>
          ))}
        </motion.div>

        {/* Filters - Animated */}
        <motion.div
          className="flex items-center gap-[var(--spacing-24)] w-full max-md:flex-col max-md:gap-[var(--spacing-16)]"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Category Dropdown */}
          <motion.div variants={filterVariants} className="relative flex-1 max-md:w-full">
            <select
              value={category ?? ''}
              onChange={(e) => handleCategoryChange(e.target.value)}
              className={cn(
                'w-full h-[56px] px-[var(--spacing-16)] py-[var(--spacing-16)] bg-white border rounded-8 font-body text-[16px] leading-[24px] appearance-none cursor-pointer focus:outline-none focus:border-blue-800 transition-colors duration-200',
                category ? 'text-blue-1000 border-blue-1000' : 'text-gray-600 border-gray-200'
              )}
              aria-label="Filter by category"
            >
              <option value="">Category</option>
              {categories.map((cat: Category) => (
                <option key={cat._id} value={cat.slug}>
                  {cat.title}
                </option>
              ))}
            </select>
            <ChevronDownIcon className="absolute right-[var(--spacing-16)] top-1/2 -translate-y-1/2 pointer-events-none text-gray-600" />
          </motion.div>

          {/* Author Dropdown - Static for now */}
          <motion.div variants={filterVariants} className="relative flex-1 max-md:w-full">
            <select
              value={author ?? ''}
              onChange={(e) => handleAuthorChange(e.target.value)}
              className={cn(
                'w-full h-[56px] px-[var(--spacing-16)] py-[var(--spacing-16)] bg-white border rounded-8 font-body text-[16px] leading-[24px] appearance-none cursor-pointer focus:outline-none focus:border-blue-800 transition-colors duration-200',
                author ? 'text-blue-1000 border-blue-1000' : 'text-gray-600 border-gray-200'
              )}
              aria-label="Filter by author"
            >
              <option value="">Author</option>
              {authors.map((a: AuthorListItem) => (
                <option key={a._id} value={a.slug}>
                  {a.name}
                </option>
              ))}
            </select>
            <ChevronDownIcon className="absolute right-[var(--spacing-16)] top-1/2 -translate-y-1/2 pointer-events-none text-gray-600" />
          </motion.div>

          {/* Search Input */}
          <motion.div variants={filterVariants} className="relative flex-1 max-md:w-full">
            <input
              type="text"
              placeholder="Search"
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              className={cn(
                'w-full h-[56px] px-[var(--spacing-16)] py-[var(--spacing-16)] pr-[48px] bg-white border rounded-8 font-body text-[16px] leading-[24px] text-blue-1000 placeholder:text-gray-600 focus:outline-none focus:border-blue-800 transition-colors duration-200',
                searchInput ? 'border-blue-1000' : 'border-gray-200'
              )}
              aria-label="Search resources"
            />
            {searchInput ? (
              <button
                onClick={handleClearSearch}
                className="absolute right-[var(--spacing-16)] top-1/2 -translate-y-1/2 text-blue-1000 hover:text-blue-800 transition-colors"
                aria-label="Clear search"
              >
                <CloseIcon />
              </button>
            ) : (
              <SearchIcon className="absolute right-[var(--spacing-16)] top-1/2 -translate-y-1/2 pointer-events-none text-gray-600" />
            )}
          </motion.div>
        </motion.div>
      </section>

      {/* Active Filters Summary */}
      {hasActiveFilters && !showEmptyState && (
        <motion.section
          className="flex justify-center px-[var(--spacing-24)] w-full max-md:px-[var(--spacing-16)]"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex items-center justify-between w-full max-w-[1016px] py-[var(--spacing-16)] border-b border-gray-200">
            <Typography as="span" variant="small" className="text-blue-1000/70">
              {filteredArticles.length} result{filteredArticles.length !== 1 ? 's' : ''} found
            </Typography>
            <motion.button
              onClick={handleClearAllFilters}
              className="inline-flex items-center gap-[var(--spacing-8)] font-body text-[14px] leading-[20px] font-medium text-blue-800 hover:text-blue-900 transition-colors duration-200"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <CloseIcon className="w-[16px] h-[16px]" />
              Clear all filters
            </motion.button>
          </div>
        </motion.section>
      )}

      {/* Empty State */}
      {showEmptyState && (
        <section className="flex justify-center px-[var(--spacing-24)] py-[var(--spacing-64)] w-full max-md:px-[var(--spacing-16)]">
          <EmptyState searchTerm={search} onClearFilters={handleClearAllFilters} />
        </section>
      )}

      {/* Featured Post - Show only when no filters active and we have articles */}
      {featuredArticle && (
        <motion.section
          className="flex justify-center px-[var(--spacing-24)] py-[var(--spacing-32)] w-full max-md:px-[var(--spacing-16)]"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <FeaturedCard
            imageSrc={featuredArticle.featuredImage?.url || '/img/resources/placeholder.jpg'}
            title={featuredArticle.title}
            description={featuredArticle.excerpt || ''}
            author={featuredArticle.author?.name || 'Unknown Author'}
            tags={formatCategoriesAsTags(featuredArticle.categories)}
            href={`/resources/${featuredArticle.slug}`}
            onClick={() =>
              trackEvent('article_clicked', {
                slug: featuredArticle.slug,
                title: featuredArticle.title,
                type: featuredArticle.articleType,
                category: formatCategoriesAsTags(featuredArticle.categories),
                is_featured: true,
              })
            }
          />
        </motion.section>
      )}

      {/* Blog Cards Grid */}
      {displayArticles.length > 0 && (
        <section className="flex justify-center px-[var(--spacing-24)] py-[var(--spacing-32)] w-full max-md:px-[var(--spacing-16)]">
          <motion.div
            key={`grid-${type ?? ''}-${category ?? ''}-${author ?? ''}`}
            className="grid grid-cols-3 gap-[var(--spacing-24)] w-full max-w-[1016px] max-lg:grid-cols-2 max-md:grid-cols-1"
            variants={cardContainerVariants}
            initial="hidden"
            animate={hasActiveFilters ? 'visible' : undefined}
            whileInView={hasActiveFilters ? undefined : 'visible'}
            viewport={{ once: true, amount: 0.1 }}
          >
            {displayArticles.map((article: ArticleCard) => (
              <motion.div key={article._id} variants={cardVariants} className="h-full">
                <BlogCard
                  imageSrc={article.featuredImage?.url || '/img/resources/placeholder.jpg'}
                  title={article.title}
                  excerpt={article.excerpt || ''}
                  author={article.author?.name || 'Unknown Author'}
                  tags={formatCategoriesAsTags(article.categories)}
                  href={`/resources/${article.slug}`}
                  className="max-w-none"
                  onClick={() =>
                    trackEvent('article_clicked', {
                      slug: article.slug,
                      title: article.title,
                      type: article.articleType,
                      category: formatCategoriesAsTags(article.categories),
                    })
                  }
                />
              </motion.div>
            ))}
          </motion.div>
        </section>
      )}

      {/* Signup Callout - Always show unless empty state */}
      {!showEmptyState && (
        <motion.section
          className="flex justify-center px-[var(--spacing-24)] py-[var(--spacing-32)] w-full max-md:px-[var(--spacing-16)]"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
        >
          <SignupCallout />
        </motion.section>
      )}

      {/* Hire Archie CTA */}
      <HireArchieCTA />
    </div>
  );
};

// Empty State Component
const EmptyState = ({
  searchTerm,
  onClearFilters,
}: {
  searchTerm?: string;
  onClearFilters: () => void;
}) => (
  <motion.div
    className="flex flex-col items-center gap-[var(--spacing-24)] p-[var(--spacing-24)]"
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.4 }}
  >
    <div className="flex items-center gap-[var(--spacing-16)]">
      <NoResultsIcon className="w-[80px] h-[80px] text-gray-400 shrink-0" />
      <div className="flex flex-col gap-[var(--spacing-4)]">
        <Typography as="h3" variant="h3" className="text-blue-1000">
          No matches found.
        </Typography>
        <Typography as="p" variant="small" className="text-blue-1000/70">
          {searchTerm
            ? `The search "${searchTerm}" did not return any results. Please try again.`
            : 'No resources match your current filters. Please try different criteria.'}
        </Typography>
      </div>
    </div>
    <motion.button
      onClick={onClearFilters}
      className="inline-flex items-center justify-center px-[var(--spacing-24)] py-[var(--spacing-12)] rounded-8 font-body text-[16px] leading-[24px] font-normal bg-blue-1000 text-white hover:bg-blue-900 transition-colors duration-200"
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      Clear all filters
    </motion.button>
  </motion.div>
);

// Icon Components
const ChevronDownIcon = ({ className }: { className?: string }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const SearchIcon = ({ className }: { className?: string }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const CloseIcon = ({ className }: { className?: string }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const NoResultsIcon = ({ className }: { className?: string }) => (
  <svg
    width="80"
    height="80"
    viewBox="0 0 80 80"
    fill="none"
    className={className}
    aria-hidden="true"
  >
    <circle cx="35" cy="35" r="20" stroke="currentColor" strokeWidth="2.5" />
    <line x1="50" y1="50" x2="65" y2="65" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    <line x1="28" y1="28" x2="42" y2="42" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <line x1="42" y1="28" x2="28" y2="42" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);
